import { useCallback, useEffect, useRef, useState } from "react";
import { useAppRuntime } from "../providers/AppRuntimeProvider";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface LogOrigin {
  readonly kind: "source" | "module" | "service";
  readonly id: string;
}

export interface LogEntity {
  readonly id: string;
  readonly timestamp: string;
  readonly level: "debug" | "info" | "warn" | "error";
  readonly category: "sync" | "index" | "auth" | "network" | "system";
  readonly origin: LogOrigin;
  readonly message: string;
  readonly metadata: Record<string, unknown> | null;
  readonly show_in_status: boolean;
}

export interface ErrorGroup {
  readonly entry: LogEntity;
  readonly count: number;
  readonly first_seen: string;
  readonly last_seen: string;
}

/** Sync progress per source — the typed SAME-UNIT pair from `sync.progress`.
 *  `discovered` is the connector's cumulative primary-item count (latest
 *  event wins — NEVER an accumulated envelope sum: `ingested` counts
 *  envelopes per cycle and double-counts across reconnects, and pairing it
 *  with `total` was the "62,548 / 2,593" StatusBar lie). */
export interface SyncProgress {
  readonly sourceId: string;
  readonly moduleId: string;
  /** Cumulative primary items discovered (same units as `total`); 0 = the
   *  source has not reported one. */
  readonly discovered: number;
  /** Estimated total primary items (from source, if available). */
  readonly total: number | null;
  readonly phase: string;
  /** Latest status message (from app.status). */
  readonly message: string | null;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

const MAX_BUFFER = 200;

/** Index progress — separate from sync, shown as permanent indicator. */
export interface IndexProgress {
  readonly indexed: number;
  readonly total: number;
}

export interface UseAppLogsResult {
  /** Latest status message to show in StatusBar (replaces previous). */
  readonly latestStatus: { readonly sourceId: string; readonly message: string } | null;
  /** Index progress — separate permanent indicator. */
  readonly indexProgress: IndexProgress | null;
  /** Accumulated sync progress per source (source_id → SyncProgress). */
  readonly syncProgress: ReadonlyMap<string, SyncProgress>;
  /** Deduplicated error groups (from backend). */
  readonly errorGroups: readonly ErrorGroup[];
  /** Total error group count (for badge). */
  readonly errorCount: number;
  /** Query full log history via RPC (for LogViewer). */
  readonly queryLogs: (params?: Record<string, unknown>) => Promise<{
    logs: readonly LogEntity[];
    total: number;
  }>;
}

/**
 * Centralized log consumer.
 *
 * - On mount: calls RPC `logs.recent` for initial state
 * - Real-time: subscribes to `app.status` + `app.error` + `sync.progress`
 * - Accumulates sync progress (running total of ingested items per source)
 */
export function useAppLogs(): UseAppLogsResult {
  const runtime = useAppRuntime();
  const [syncProgress, setSyncProgress] = useState<
    ReadonlyMap<string, SyncProgress>
  >(new Map());
  const [latestStatus, setLatestStatus] = useState<{
    readonly sourceId: string;
    readonly message: string;
  } | null>(null);
  const [indexProgress, setIndexProgress] = useState<IndexProgress | null>(
    null,
  );
  const [errorGroups, setErrorGroups] = useState<readonly ErrorGroup[]>([]);
  const mountedRef = useRef(true);

  // Fetch initial state on mount
  useEffect(() => {
    mountedRef.current = true;

    runtime.transport
      .rpc("logs.recent", { limit: 50 })
      .then((raw: unknown) => {
        const result = raw as Record<string, unknown>;
        if (!mountedRef.current) return;

        // Initial error groups
        const groups = (result.error_groups ?? []) as readonly ErrorGroup[];
        setErrorGroups(groups);
      })
      .catch(() => {
        // RPC not available yet — will populate from events
      });

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    return () => {
      mountedRef.current = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Subscribe to real-time events
  useEffect(() => {
    // app.status — status bar messages from sources/modules
    const offStatus = runtime.transport.onEventType(
      ["app.status"],
      (event) => {
        const wrapper = event.payload as { payload: LogEntity };
        const log = wrapper.payload;
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (!log?.origin) return;
        setSyncProgress((prev) => {
          const next = new Map(prev);
          const existing = next.get(log.origin.id);
          next.set(log.origin.id, {
            sourceId: log.origin.id,
            moduleId: log.origin.id,
            discovered: existing?.discovered ?? 0,
            total: existing?.total ?? null,
            phase: existing?.phase ?? "syncing",
            message: log.message,
          });
          return next;
        });
        setLatestStatus({ sourceId: log.origin.id, message: log.message });
      },
    );

    // sync.progress — per-cycle events from the scheduler. The event carries
    // the typed SAME-UNIT pair (`discovered` = cumulative primary items,
    // `total` = primary items); latest event wins. `ingested` counts the
    // cycle's ENVELOPES — it is deliberately never read here: accumulating it
    // and pairing with total produced the "62,548 / 2,593" mixed-unit lie.
    const offSync = runtime.transport.onEventType(
      ["sync.progress"],
      (event) => {
        const payload = event.payload as {
          source_id?: string;
          module_id?: string;
          discovered?: number | null;
          total?: number | null;
          phase?: string;
        };
        const sourceId = payload.source_id ?? payload.module_id ?? "unknown";
        const phase = payload.phase ?? "syncing";
        const reportedDiscovered =
          payload.discovered != null && payload.discovered > 0 ? payload.discovered : null;
        const reportedTotal =
          payload.total != null && payload.total > 0 ? payload.total : null;
        setSyncProgress((prev) => {
          const next = new Map(prev);
          const existing = next.get(sourceId);
          // A cycle that reported nothing keeps the last known counts —
          // a catchup event must not wipe the bootstrap numbers.
          const discovered = reportedDiscovered ?? existing?.discovered ?? 0;
          const total = reportedTotal ?? existing?.total ?? null;
          next.set(sourceId, {
            sourceId,
            moduleId: payload.module_id ?? sourceId,
            discovered,
            total,
            phase,
            message: existing?.message ?? null,
          });
          // Numeric status ONLY from the same-unit pair; without a reported
          // `discovered` there is no number to show (status text stays).
          if (discovered > 0) {
            const msg =
              total != null && total > 0
                ? `${discovered.toLocaleString()} / ${total.toLocaleString()}`
                : `${discovered.toLocaleString()} synced`;
            setLatestStatus({ sourceId, message: msg });
          }
          return next;
        });
      },
    );

    // app.error — deduplicated error groups
    const offError = runtime.transport.onEventType(
      ["app.error"],
      (event) => {
        const wrapper = event.payload as {
          payload: {
            entry: LogEntity;
            count: number;
            first_seen: string;
            last_seen: string;
          };
        };
        const payload = wrapper.payload;
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (!payload?.entry) return;
        setErrorGroups((prev) => {
          const existing = prev.findIndex(
            (g) =>
              g.entry.origin.kind === payload.entry.origin.kind &&
              g.entry.origin.id === payload.entry.origin.id &&
              g.entry.message === payload.entry.message,
          );
          if (existing >= 0) {
            const next = [...prev];
            next[existing] = {
              entry: payload.entry,
              count: payload.count,
              first_seen: payload.first_seen,
              last_seen: payload.last_seen,
            };
            return next;
          }
          return [
            {
              entry: payload.entry,
              count: payload.count,
              first_seen: payload.first_seen,
              last_seen: payload.last_seen,
            },
            ...prev,
          ].slice(0, MAX_BUFFER);
        });
      },
    );

    // app.index_progress — dedicated indexer progress (separate indicator)
    const offIndex = runtime.transport.onEventType(
      ["app.index_progress"],
      (event) => {
        const payload = event.payload as {
          indexed?: number;
          total?: number;
        };
        if (payload.indexed != null && payload.total != null) {
          setIndexProgress({
            indexed: payload.indexed,
            total: payload.total,
          });
        }
      },
    );

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    return () => {
      offStatus();
      offSync();
      offError();
      offIndex();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const queryLogs = useCallback(
    async (
      params: Record<string, unknown> = {},
    ): Promise<{ logs: readonly LogEntity[]; total: number }> => {
      const result = await runtime.transport.rpc<{ logs: readonly LogEntity[]; total: number }>("logs.query", params);
      return result;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return {
    latestStatus,
    indexProgress,
    syncProgress,
    errorGroups,
    errorCount: errorGroups.reduce((sum, g) => sum + g.count, 0),
    queryLogs,
  };
}
